/** 录音只在内存中流转；离开页面、关闭编辑器和迟到的权限结果都必须关掉麦克风。 */
export const VOICE_MIN_MS = 5_000;
export const VOICE_MAX_MS = 10_000;
export const VOICE_MAX_BYTES = 2 * 1024 * 1024;
const PCM_RATE = 16_000;
const STOP_TOLERANCE_MS = 250;
const MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

export interface VoiceClip {
  blob: Blob;
  durationMs: number;
  mimeType: 'audio/wav';
}

export interface VoiceState {
  phase: 'idle' | 'requesting' | 'recording' | 'stopping' | 'ready' | 'error';
  elapsedMs: number;
  clip?: VoiceClip;
  error?: string;
}

export interface VoiceRecorder {
  readonly state: VoiceState;
  /** 必须由人点击「录一段」调用；创建控制器本身不申请麦克风。 */
  start(): Promise<void>;
  stop(): void;
  cancel(): void;
  dispose(): void;
}

interface Capture {
  readonly mimeType: string;
  start(): void;
  stop(): void;
  dispose(): void;
}

export interface VoiceInput {
  stop(): void;
  recorder(mimeType: string, events: { data(blob: Blob): void; stopped(): void; failed(): void }): Capture;
}

export interface VoiceRecorderDeps {
  available(): boolean;
  supports(mimeType: string): boolean;
  requestInput(): Promise<VoiceInput>;
  now(): number;
  hidden(): boolean;
  onHidden(callback: () => void): () => void;
  after(callback: () => void, ms: number): number;
  clear(id: number): void;
  normalize(blob: Blob): Promise<VoiceClip>;
}

export interface VoicePcm {
  readonly sampleRate: number;
  readonly length: number;
  readonly numberOfChannels: number;
  getChannelData(channel: number): Float32Array;
}

/** 浏览器解码时已重采样；这里仅把声道混成单声道并写成服务端可验证的 PCM WAV。 */
export function encodeVoicePcm(pcm: VoicePcm): VoiceClip {
  if (pcm.sampleRate !== PCM_RATE || !Number.isInteger(pcm.length) || !Number.isInteger(pcm.numberOfChannels) || pcm.numberOfChannels < 1 || pcm.numberOfChannels > 8) {
    throw new Error('这段声音读不出来，请重新录一段');
  }
  const duration = pcm.length / PCM_RATE * 1000;
  if (duration < VOICE_MIN_MS) throw new Error('录音至少需要 5 秒，请重新录一段');
  if (duration > VOICE_MAX_MS + STOP_TOLERANCE_MS) throw new Error('录音中断了，请重新录一段 5–10 秒的声音');
  // 编码帧尾和停止任务可能略晚；只裁掉十秒之后的尾巴，不补造不足五秒的声音。
  const samples = Math.min(pcm.length, PCM_RATE * VOICE_MAX_MS / 1000);
  const channels = Array.from({ length: pcm.numberOfChannels }, (_, i) => pcm.getChannelData(i));
  if (channels.some((channel) => channel.length !== pcm.length)) throw new Error('这段声音读不出来，请重新录一段');
  const bytes = new ArrayBuffer(44 + samples * 2), view = new DataView(bytes);
  const ascii = (at: number, value: string) => [...value].forEach((char, i) => view.setUint8(at + i, char.charCodeAt(0)));
  ascii(0, 'RIFF');
  view.setUint32(4, bytes.byteLength - 8, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, PCM_RATE, true);
  view.setUint32(28, PCM_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, samples * 2, true);
  for (let i = 0; i < samples; i++) {
    let value = 0;
    for (const channel of channels) value += channel[i];
    if (!Number.isFinite(value)) throw new Error('这段声音读不出来，请重新录一段');
    value = Math.max(-1, Math.min(1, value / channels.length));
    view.setInt16(44 + i * 2, Math.round(value * (value < 0 ? 0x8000 : 0x7fff)), true);
  }
  return { blob: new Blob([bytes], { type: 'audio/wav' }), durationMs: samples / PCM_RATE * 1000, mimeType: 'audio/wav' };
}

/** OfflineAudioContext 的解码按规范重采样，不创建扬声器输出，也不保存原始录音。 */
export async function normalizeVoice(blob: Blob, decode: (data: ArrayBuffer) => Promise<VoicePcm> = (data) => new OfflineAudioContext(1, 1, PCM_RATE).decodeAudioData(data)): Promise<VoiceClip> {
  if (!blob.size) throw new Error('没有录到声音，请重新录一段');
  if (blob.size > VOICE_MAX_BYTES) throw new Error('这段录音太大了，请重新录一段');
  let decoded: VoicePcm;
  try {
    decoded = await decode(await blob.arrayBuffer());
  } catch {
    throw new Error('这个浏览器没有读出录音，请换一个浏览器再试');
  }
  return encodeVoicePcm(decoded);
}

function browserDeps(): VoiceRecorderDeps {
  return {
    available: () => window.isSecureContext && typeof MediaRecorder !== 'undefined' && typeof OfflineAudioContext !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
    supports: (mime) => MediaRecorder.isTypeSupported(mime),
    async requestInput() {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 }, video: false });
      return {
        stop: () => stream.getTracks().forEach((track) => track.stop()),
        recorder(mimeType, events) {
          const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 96_000 });
          recorder.ondataavailable = (event) => events.data(event.data);
          recorder.onstop = events.stopped;
          recorder.onerror = events.failed;
          return {
            get mimeType() { return recorder.mimeType; },
            start: () => recorder.start(250),
            stop: () => { if (recorder.state !== 'inactive') recorder.stop(); },
            dispose() {
              recorder.ondataavailable = recorder.onstop = recorder.onerror = null;
              if (recorder.state !== 'inactive') recorder.stop();
            },
          };
        },
      };
    },
    now: () => performance.now(),
    hidden: () => document.hidden,
    onHidden(callback) {
      const visibility = () => { if (document.hidden) callback(); };
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('pagehide', callback);
      return () => {
        document.removeEventListener('visibilitychange', visibility);
        window.removeEventListener('pagehide', callback);
      };
    },
    after: (callback, ms) => window.setTimeout(callback, ms),
    clear: (id) => window.clearTimeout(id),
    normalize: normalizeVoice,
  };
}

function failureMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return '没有获得麦克风权限。允许后可再点一次「录一段」';
    if (error.name === 'NotFoundError') return '没有找到麦克风，接好后再试';
    if (error.name === 'NotReadableError') return '麦克风正在忙，关闭其他录音后再试';
  }
  return '录音没有开始，请再试一次';
}

export function createVoiceRecorder(options: { onChange(state: VoiceState): void; holdSound?: () => () => void }, deps: VoiceRecorderDeps = browserDeps()): VoiceRecorder {
  let state: VoiceState = { phase: 'idle', elapsedMs: 0 };
  let generation = 0, disposed = false, startedAt = 0, timer: number | null = null;
  let input: VoiceInput | null = null, capture: Capture | null = null, releaseSound: (() => void) | null = null;
  let chunks: Blob[] = [], totalBytes = 0;

  function publish(next: VoiceState): void {
    state = next;
    if (!disposed) options.onChange(next);
  }

  function stopInput(): void {
    if (timer !== null) deps.clear(timer);
    timer = null;
    input?.stop();
    input = null;
    releaseSound?.();
    releaseSound = null;
  }

  function abandon(): void {
    generation++;
    capture?.dispose();
    capture = null;
    stopInput();
    chunks = [];
    totalBytes = 0;
  }

  function fail(error: string): void {
    abandon();
    publish({ phase: 'error', elapsedMs: 0, error });
  }

  function stop(): void {
    if (state.phase !== 'recording' || !capture) return;
    const elapsedMs = deps.now() - startedAt;
    if (elapsedMs < VOICE_MIN_MS) return fail('录音至少需要 5 秒，请重新录一段');
    if (elapsedMs > VOICE_MAX_MS + STOP_TOLERANCE_MS) return fail('录音中断了，请重新录一段 5–10 秒的声音');
    publish({ phase: 'stopping', elapsedMs: Math.min(elapsedMs, VOICE_MAX_MS) });
    try {
      capture.stop();
    } catch {
      return fail('录音中断了，请重新录一段');
    }
    stopInput();
  }

  function tick(): void {
    if (state.phase !== 'recording') return;
    if (deps.hidden()) return fail('离开页面后录音已取消，请重新录一段');
    const elapsedMs = deps.now() - startedAt;
    if (elapsedMs >= VOICE_MAX_MS) return stop();
    publish({ phase: 'recording', elapsedMs });
    timer = deps.after(tick, Math.min(100, VOICE_MAX_MS - elapsedMs));
  }

  const unsubscribe = deps.onHidden(() => {
    if (state.phase === 'requesting' || state.phase === 'recording' || state.phase === 'stopping') fail('离开页面后录音已取消，请重新录一段');
  });

  return {
    get state() { return state; },
    async start() {
      if (disposed || ['requesting', 'recording', 'stopping'].includes(state.phase)) return;
      abandon();
      if (!deps.available()) return fail('这个页面不能录音，请用 HTTPS 地址在支持录音的浏览器中打开');
      if (deps.hidden()) return fail('请回到页面后再开始录音');
      const mimeType = MIME_TYPES.find((mime) => deps.supports(mime));
      if (!mimeType) return fail('这个浏览器不支持录音，请换一个浏览器再试');
      const id = generation;
      publish({ phase: 'requesting', elapsedMs: 0 });
      try {
        releaseSound = options.holdSound?.() ?? null;
        const acquired = await deps.requestInput();
        if (disposed || id !== generation || deps.hidden()) {
          acquired.stop();
          if (id === generation && !disposed) fail('离开页面后录音已取消，请重新录一段');
          return;
        }
        input = acquired;
        const active = () => !disposed && generation === id;
        capture = acquired.recorder(mimeType, {
          data(blob) {
            if (!active() || !blob.size) return;
            totalBytes += blob.size;
            if (totalBytes > VOICE_MAX_BYTES) return fail('这段录音太大了，请重新录一段');
            chunks.push(blob);
          },
          failed() { if (active()) fail('录音中断了，请重新录一段'); },
          stopped() {
            if (!active()) return;
            // 硬件断开也会触发 stop，不能把未主动结束的短片段当成完整录音。
            if (state.phase !== 'stopping') return fail('录音中断了，请重新录一段');
            const blob = new Blob(chunks, { type: capture?.mimeType || mimeType });
            capture?.dispose();
            capture = null;
            chunks = [];
            stopInput();
            void deps.normalize(blob).then((clip) => {
              if (active()) publish({ phase: 'ready', elapsedMs: clip.durationMs, clip });
            }, (error: unknown) => {
              if (active()) fail(error instanceof Error ? error.message : '这段声音读不出来，请重新录一段');
            });
          },
        });
        startedAt = deps.now();
        capture.start();
        publish({ phase: 'recording', elapsedMs: 0 });
        timer = deps.after(tick, 100);
      } catch (error) {
        if (generation === id && !disposed) fail(failureMessage(error));
      }
    },
    stop,
    cancel() {
      abandon();
      publish({ phase: 'idle', elapsedMs: 0 });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      abandon();
      state = { phase: 'idle', elapsedMs: 0 };
      unsubscribe();
    },
  };
}
